import { sessionStore } from "@auvious/utils";

import { KEY_GENESYS_TOKEN, PARAM_GENESYS_TOKEN } from "../app.enums";
import { IReusableCredential } from "../../core-ui/models/OAuth2CodeGrantAuthenticationServiceOptions";

/**
 * how long a genesys token stays worth presenting before the agent has to sign in again.
 *
 * the token itself may well outlive this, but it is not ours to renew: the further it is from the
 * sign-in that minted it, the likelier it has been revoked or has idled out, and a working day is
 * the span this is meant to cover.
 */
export const GENESYS_TOKEN_MAX_AGE_MS = 18 * 60 * 60 * 1000;

interface IStoredGenesysToken {
  token: string;
  /** epoch milliseconds, so utc wherever the agent is */
  storedAt: number;
}

/**
 * keeps the genesys token an auvious session was built on, so that the next session can be had
 * without the sign-in popup: rtc-auth takes the token in place of an authorization code.
 *
 * it lives in session storage, which already holds an auvious token carrying this very same
 * genesys token inside it - so nothing new is exposed - and, being per tab, it goes when the
 * agent's genesys workspace tab does.
 */
export class GenesysTokenCredentialStore implements IReusableCredential {
  load(): { param: string; value: string } | null {
    const stored = this.read();

    if (!stored) {
      return null;
    }

    if (Date.now() - stored.storedAt >= GENESYS_TOKEN_MAX_AGE_MS) {
      this.clear();
      return null;
    }

    return { param: PARAM_GENESYS_TOKEN, value: stored.token };
  }

  /**
   * remember the genesys token a token response carried.
   *
   * every refresh, and every session established with the token itself, hands the same genesys
   * token straight back. re-stamping it each time would slide the window forward for ever and it
   * would never age out, so only a token we have not seen before starts a new one.
   */
  store(token: string) {
    if (!token || this.read()?.token === token) {
      return;
    }

    try {
      sessionStore.setItem(
        KEY_GENESYS_TOKEN,
        JSON.stringify({ token, storedAt: Date.now() } as IStoredGenesysToken)
      );
    } catch {}
  }

  clear() {
    try {
      sessionStore.removeItem(KEY_GENESYS_TOKEN);
    } catch {}
  }

  private read(): IStoredGenesysToken | null {
    try {
      const stored = JSON.parse(
        sessionStore.getItem(KEY_GENESYS_TOKEN)
      ) as IStoredGenesysToken;

      return !!stored?.token && !!stored?.storedAt ? stored : null;
    } catch {
      return null;
    }
  }
}
